// src/components/RequireAuth.tsx
import { Navigate, Outlet, useLocation } from 'react-router'
import { SgdsButton, SgdsSpinner } from '@govtechsg/sgds-web-component/react'
import { useAuth } from '../state/AuthContext'
import { SsoRedirect } from './SsoRedirect'

export function RequireAuth() {
  const { status, unreachableReason, retry, signedOutDeliberately } = useAuth()
  const location = useLocation()

  if (status === 'checking') {
    return (
      <div className="min-h-screen grid place-items-center">
        <div className="flex flex-col items-center gap-3">
          <SgdsSpinner size="lg" label="Checking your session…" orientation="vertical" />
        </div>
      </div>
    )
  }

  if (status === 'unreachable') {
    return (
      <div className="min-h-screen grid place-items-center px-6">
        <div className="pt-card max-w-md text-center" role="alert">
          <p className="font-semibold text-[var(--sgds-danger-color-emphasis)]">PRS could not be reached</p>
          <p className="mt-2 text-[var(--sgds-body-color-subtle)]">{unreachableReason}</p>
          <p className="mt-2 text-sm text-[var(--sgds-color-subtle)]">
            Start it with <code className="pt-mono">pnpm dev</code> in <code className="pt-mono">services/gateway</code>.
          </p>
          <div className="mt-4">
            <SgdsButton variant="outline" type="button" onClick={retry}>
              Retry
            </SgdsButton>
          </div>
        </div>
      </div>
    )
  }

  if (status === 'signed-out') {
    /**
     * ★★★ S-079 — `from` IS CAPTURED FOR A BOUNCE, NOT FOR A SIGN-OUT.
     *
     * Carrying `from` is the deep-link feature and it stays: open `/bills/X` while
     * signed out, land here, sign in, arrive at `/bills/X`.
     *
     * ⚠ **But this branch runs for EVERY transition to `signed-out`, including a
     * deliberate `logout()`** — so it also captured the page the OUTGOING officer
     * was on, and `LoginPage` then sent the NEXT officer to sign in straight there.
     * Found by running the browser journeys: officer A referred `BILL-NUH-09`,
     * signed out, and officer B's first screen after signing in was
     * `/bills/BILL-NUH-09`. If B holds no grant over that bill their first screen is
     * a refusal that reads as "my access is broken"; and it puts a bill reference
     * from another officer's session in B's own URL bar, on a machine two officers
     * share — which a demonstration laptop is.
     *
     * ⚠ **GATED AT THE POINT OF CAPTURE, and the first attempt gated at the point of
     * CONSUMPTION instead — which could not work.** `LoginPage` reading the same
     * flag is a no-op: `adopt()` clears it in the same commit that sets
     * `signed-in`, so by the time the redirect is decided the flag is already false
     * and `from` is honoured anyway. The browser journey still failed, identically,
     * which is what showed it. Not capturing the value is the only version of this
     * that has no ordering to get wrong.
     *
     * ⚠ It cannot be carried in router state either: the `navigate('/login')` in
     * `Shell`'s sign-out handler and this `<Navigate>` race, and whichever lands
     * last owns `location.state`. A context flag set before the status change does
     * not race.
     */
    // ★ ADR 0015 — a deliberate sign-out lands on a page that says so and waits (it must not bounce straight back into single
    // sign-on, or signing out would do nothing); anything else goes to single sign-on at once and returns to the page asked for.
    // A deployment with no identity provider falls back to the `/login` page inside `SsoRedirect`.
    return signedOutDeliberately ? <Navigate to="/signed-out" replace /> : <SsoRedirect returnTo={location.pathname + location.search} />
  }

  return <Outlet />
}
